import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import type React from "react";
import { useCallback } from "react";

/**
 * The rename input's wiring for a thread row: focus on mount, commit on Enter
 * or blur, cancel on Escape. Extracted from `useSidebarThreadRow` so the row
 * hook stays under its complexity budget; the guards and the committed-ref
 * dance are exactly what they were, one file over.
 */
export function useSidebarThreadRename(input: {
  readonly threadRef: ScopedThreadRef;
  readonly renamingInputRef: React.MutableRefObject<HTMLInputElement | null>;
  readonly renamingCommittedRef: React.MutableRefObject<boolean>;
  readonly setRenamingTitle: (title: string) => void;
  readonly renamingTitle: string;
  readonly commitRename: (
    threadRef: ScopedThreadRef,
    newTitle: string,
    originalTitle: string,
  ) => Promise<void>;
  readonly cancelRename: () => void;
  readonly originalTitle: string;
}) {
  const {
    threadRef,
    renamingInputRef,
    renamingCommittedRef,
    setRenamingTitle,
    renamingTitle,
    commitRename,
    cancelRename,
    originalTitle,
  } = input;

  const handleRenameInputRef = useCallback(
    (element: HTMLInputElement | null) => {
      if (element && renamingInputRef.current !== element) {
        renamingInputRef.current = element;
        element.focus();
        element.select();
      }
    },
    [renamingInputRef],
  );

  const handleRenameInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setRenamingTitle(event.target.value),
    [setRenamingTitle],
  );

  const handleRenameInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      event.stopPropagation();
      // An IME confirmation also arrives as Enter; committing then would save
      // the half-composed title while the reader is still picking a candidate.
      // Synthetic events in tests carry no nativeEvent, so absence commits.
      if (event.key === "Enter" && !event.nativeEvent?.isComposing) {
        event.preventDefault();
        renamingCommittedRef.current = true;
        void commitRename(threadRef, renamingTitle, originalTitle);
      } else if (event.key === "Escape") {
        event.preventDefault();
        renamingCommittedRef.current = true;
        cancelRename();
      }
    },
    [cancelRename, commitRename, renamingCommittedRef, renamingTitle, originalTitle, threadRef],
  );

  const handleRenameInputBlur = useCallback(() => {
    if (!renamingCommittedRef.current) {
      void commitRename(threadRef, renamingTitle, originalTitle);
    }
  }, [commitRename, renamingCommittedRef, renamingTitle, originalTitle, threadRef]);

  const handleRenameInputClick = useCallback(
    (event: React.MouseEvent<HTMLInputElement>) => event.stopPropagation(),
    [],
  );

  return {
    handleRenameInputRef,
    handleRenameInputChange,
    handleRenameInputKeyDown,
    handleRenameInputBlur,
    handleRenameInputClick,
  };
}
